<template>
  <div class="center-of-mass-motion-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>F_ext = M · a_cm | 内力不改变质心运动$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Ground -->
          <line x1="0" y1="220" x2="400" y2="220" stroke="#444" stroke-width="2" />

          <!-- System (Barbell-like structure) -->
          <g :transform="`translate(${x_cm}, ${y_cm}) rotate(${angle})`">
             <!-- Connecting Rod -->
             <line :x1="-dist/2" y1="0" :x2="dist/2" y2="0" stroke="#888" stroke-width="2" />
             
             <!-- Mass 1 -->
             <circle :cx="-dist/2" cy="0" :r="m1_r" fill="#ff4757" />
             <text :x="-dist/2 - 10" y="-15" fill="#ff4757" font-size="8">m1</text>
             
             <!-- Mass 2 -->
             <circle :cx="dist/2" cy="0" :r="m2_r" fill="#1e90ff" />
             <text :x="dist/2 - 10" y="-15" fill="#1e90ff" font-size="8">m2</text>
          </g>

          <!-- Center of Mass Marker (Always follows parabola/linear even if rotating) -->
          <g :transform="`translate(${x_cm}, ${y_cm})`">
             <circle r="4" fill="#ffd700" stroke="#fff" stroke-width="1" />
             <text x="8" y="5" fill="#ffd700" font-size="10" font-weight="bold">质心 (CM)</text>
          </g>

          <!-- Path of CM -->
          <polyline :points="cmPath" fill="none" stroke="rgba(255, 215, 0, 0.3)" stroke-dasharray="4,4" />

          <text x="20" y="30" fill="#888" font-size="8">抛体运动：只有重力（外力）决定质心轨迹</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>系统总质量 M: {{ (m1 + m2).toFixed(1) }} kg</label>
              <div class="value" style="color: #ffd700">状态: {{ active ? '平动+转动' : '准备抛出' }}</div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row items-center gap-4">
              <div class="flex-1">
                 <label>质量比 m1/m2</label>
                 <el-slider v-model="massRatio" :min="0.2" :max="5" :step="0.1" size="mini"></el-slider>
              </div>
              <el-button type="primary" size="mini" @click="launch" :disabled="active">抛出系统</el-button>
              <el-button size="mini" @click="reset">重置</el-button>
           </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>质心运动定理是动力学的基本定理之一。它指出：质点系的质心运动，就像是一个位于质心的、等于全系总质量的质点，受所有施加于该系的外力的矢量和作用时的运动。无论系统内部零件如何相互作用（内力），只要合外力不变，质心的加速度就不会改变。例如：一个在空中爆炸的烟花，在忽略空气阻力的情况下，爆炸后所有碎片的质心仍将沿着爆炸前既定的抛物线轨道运动。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CenterOfMassMotionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      massRatio: 1,
      active: false,
      t: 0,
      x0: 50, y0: 180,
      vx: 4, vy: -8,
      g: 0.15,
      angle: 0,
      rotationSpeed: 5,
      dist: 60,
      pathPoints: [],
      timer: null
    }
  },
  computed: {
    m1() { return 10 * this.massRatio; },
    m2() { return 10; },
    m1_r() { return 5 + Math.sqrt(this.m1) * 2; },
    m2_r() { return 5 + Math.sqrt(this.m2) * 2; },
    x_cm() {
      if (!this.active) return this.x0;
      return this.x0 + this.vx * this.t;
    },
    y_cm() {
      if (!this.active) return this.y0;
      return this.y0 + this.vy * this.t + 0.5 * this.g * this.t * this.t;
    },
    cmPath() {
      return this.pathPoints.map(p => `${p.x},${p.y}`).join(' ');
    }
  },
  methods: {
    launch() {
      this.active = true;
      this.animate();
    },
    animate() {
      if (!this.active) return;
      this.t += 1;
      this.angle += this.rotationSpeed;
      
      // Record path
      if (this.t % 2 === 0) {
        this.pathPoints.push({ x: this.x_cm, y: this.y_cm });
      }

      // Ground collision or out of bounds
      if (this.y_cm > 220 || this.x_cm > 400) {
        this.active = false;
        return;
      }
      this.timer = requestAnimationFrame(this.animate);
    },
    reset() {
      this.active = false;
      this.t = 0;
      this.angle = 0;
      this.pathPoints = [];
      cancelAnimationFrame(this.timer);
    }
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.center-of-mass-motion-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 215, 0, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
